import React, { useEffect, useState } from 'react'

import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'

import NiceButton from '../common/NiceButton'
import axiosClient from '../service/axiosClient'
import { AccountingSystem, accountingSystems } from '../types/AccountingSystem'

interface SystemCodesDto {
    system: AccountingSystem
    accountingCode: string | null
    vatClassificationCode: string | null
    // Sent by the backend so an empty field can show what the export falls back to
    defaultAccountingCode: string | null
    defaultVatClassificationCode: string | null
    maxCodeLength: number
}

interface AccountingExportSettingsDto {
    systems: SystemCodesDto[]
}

// Every accounting program names its posting codes and VAT classifications in its own lists -
// POHODA's "UD" means nothing to Money S3 - so the user sets a pair per system. Left empty, the
// export uses that system's default, shown as the placeholder
const AccountingExportSettings = () => {
    const {t} = useTranslation()
    const [systems, setSystems] = useState<SystemCodesDto[]>([])
    const [isLoading, setIsLoading] = useState(false)

    useEffect(() => {
        axiosClient.get<AccountingExportSettingsDto>('user/accounting-export').then(response => {
            setSystems(response.data.systems)
        }).catch(error => console.error('Failed to load accounting export settings', error))
    }, [])

    function change(system: AccountingSystem, field: 'accountingCode' | 'vatClassificationCode', value: string) {
        setSystems(current => current.map(codes => codes.system === system ? {...codes, [field]: value} : codes))
    }

    async function save(event: React.FormEvent) {
        event.preventDefault()
        setIsLoading(true)
        try {
            await axiosClient.put('user/accounting-export', {
                systems: systems.map(({system, accountingCode, vatClassificationCode}) =>
                    ({system, accountingCode, vatClassificationCode})),
            })
            toast.success(t('accounting_export_saved'))
        } catch (error) {
            console.error('Failed to save accounting export settings', error)
            toast.error(t('accounting_export_save_error'))
        } finally {
            setIsLoading(false)
        }
    }

    function label(system: AccountingSystem): string {
        return accountingSystems.find(option => option.value === system)?.label ?? system
    }

    return (
        <form onSubmit={save}>
            <h4 className="text-left mb-2 mt-4">{t('pohoda_export')}</h4>
            <small className="d-block text-muted mb-3">{t('pohoda_export_hint')}</small>
            {systems.map(codes => (
                <fieldset key={codes.system} className="mb-3">
                    <legend className="h6">{label(codes.system)}</legend>
                    <div className="row">
                        <div className="form-group col-md-6 mb-2">
                            <label htmlFor={`accounting-${codes.system}`}>
                                {/* Omega books a line by its account, not by a posting code */}
                                {t(codes.system === 'KROS_OMEGA' ? 'omega_revenue_account' : 'pohoda_accounting')}
                            </label>
                            <input id={`accounting-${codes.system}`} className="form-control"
                                   maxLength={codes.maxCodeLength}
                                   placeholder={codes.defaultAccountingCode ?? ''}
                                   value={codes.accountingCode ?? ''}
                                   onChange={(e) => change(codes.system, 'accountingCode', e.target.value)}/>
                        </div>
                        <div className="form-group col-md-6 mb-2">
                            <label htmlFor={`vat-${codes.system}`}>{t('pohoda_vat_classification')}</label>
                            <input id={`vat-${codes.system}`} className="form-control"
                                   maxLength={codes.maxCodeLength}
                                   placeholder={codes.defaultVatClassificationCode ?? ''}
                                   value={codes.vatClassificationCode ?? ''}
                                   onChange={(e) => change(codes.system, 'vatClassificationCode', e.target.value)}/>
                        </div>
                    </div>
                </fieldset>
            ))}
            <NiceButton type="submit" isLoading={isLoading}>
                {t('save')}
            </NiceButton>
        </form>
    )
}

export default AccountingExportSettings
